import { computed, ref, watch } from "vue"

/**
 * 在组件内创建一个可在内部控制、同时与外部双向绑定的值
 * 外部如果非双向绑定，则 model 只在初始化时生效
 */
export function useInsideModel(options) {
  const { $props, propKey, $emit, eventName } = options

  // 外部绑定值
  const modelKey = propKey ?? "modelValue"
  const updateEvent = eventName ?? `update:${modelKey}`
  const model = computed({
    get: () => $props[modelKey],
    set: (v) => $emit(updateEvent, v)
  })

  // 内部控制值
  const inside = ref(options.initValue)

  // 1. inside 变化时修改 model
  watch(inside, (inside) => {
    if (model.value !== inside) {
      model.value = inside
    }
  })

  // 2. model 变化时修改 inside
  watch(model, (model) => {
    if (inside.value !== model) {
      inside.value = model
    }
  })

  const { init = (model) => model } = options
  inside.value = init(model.value, inside.value)

  return inside
}

export function usePagination({ api, pagination = {}, params }) {
  const page = ref(pagination.page ?? 1)
  const size = ref(pagination.size ?? 10)
  const total = ref(0)
  const records = ref([])
  const loading = ref(false)

  /** 页码数 */
  const pages = computed(() => {
    return Math.ceil(total.value / size.value)
  })
  /** 获取入参 */
  function getParams() {
    if (typeof params === "function") {
      return params()
    } else {
      return params
    }
  }
  /** 请求数据 */
  function getApi() {
    const params = getParams()
    loading.value = true
    return api({ page: page.value, size: size.value, ...params })
      .then((res) => {
        const { records: r, total: t } = res
        records.value = r
        total.value = t
      })
      .finally(() => {
        loading.value = false
      })
  }
  /** 下一页 */
  function next() {
    if (page.value < pages.value) {
      page.value += 1
      return getApi()
    }
    return Promise.resolve()
  }
  /** 上一页 */
  function prev() {
    if (page.value > 1) {
      page.value -= 1
      return getApi()
    }
    return Promise.resolve()
  }

  return {
    pages,
    page,
    size,
    total,
    records,
    loading,
    getApi,
    next,
    prev
  }
}

/**
 * 封装后端分页的 table 数据请求器，移除 res.data
 * 将 { data: { records: [] } } 解析为 { records: [] }
 * @example
 * ```ts
 * // 输入
 * removeDataApi(() => Promise<{ success, data:{ records: Record[], total: 0 } }>)
 * // 输出
 * () => Promise<{records:Record[], total: 0}>
 * ```
 * */
export function removeDataApi(api) {
  return function (p) {
    return api(p).then(({ success, data }) => {
      if (success) return data
      else return { records: [], total: 0 }
    })
  }
}

/**
 * 封装前端分页的 table 数据请求器，添加 records
 * 将 {data: []} 改为 {records:[]}
 * @example
 * ```ts
 * // 输入
 * wrapRecordsApi(() => Promise<{success, data: Record[]}>)
 * // 输出
 * () => Promise<{ records: Record[], total: data.length }>
 * ```
 * */
export function wrapRecordsApi(api) {
  return function (p) {
    return api(p).then(({ success, data }) => {
      if (success) return { records: data, total: data.length }
      else return { records: [], total: 0 }
    })
  }
}
